import React, { useRef, useState } from "react";
import { Modal, Row, Col, Card, Form, Button } from "react-bootstrap";
import { useLanguage } from "../helpers/LanguageContext";
import PickerMaterial, { pickerMaterials } from "./PickerMaterial";
import PickerEmptyCard from "./PickerEmptyCard";
import { colorMaterial } from "../components/colorPaletteGroups";
import PickerPagination, {
  usePickerPage,
  PICKER_PAGE_SIZE
} from "./PickerPagination";
import { variantMode } from "../../model/millingVariants";
import MillingImage from "../components/millingsPage/MillingImage";
import usePickerKeyboardNavigation from "./PickerKeyboardNavigation";

export default function MillingPickerModal({
  onClose,
  onSelect,
  millings = [],
  millingId,
  millingVariantId = "",
  availableMillings = [],
  hasAssignment,
  children,
  onBack,
  initialMaterial,
  onMaterialChange,
  draftSelection,
  canConfirm = false
}) {
  const { t } = useLanguage();
  const currentMilling = millings.find(
    (milling) => String(milling.millingId) === String(millingId)
  );
  const [selection, setSelection] = useState(
    () =>
      draftSelection ||
      (currentMilling
        ? { milling: currentMilling, variantId: millingVariantId || "" }
        : millingId === null
          ? { milling: null, variantId: "" }
          : null)
  );
  const [search, setSearch] = useState("");
  const searchInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const materials = pickerMaterials(millings);
  const initialMillingMaterial = currentMilling
    ? colorMaterial(currentMilling)
    : initialMaterial;
  const [selectedMaterial, setSelectedMaterial] = useState(() =>
    materials.includes(initialMillingMaterial)
      ? initialMillingMaterial
      : materials[0] || ""
  );
  const [pendingMaterial, setPendingMaterial] = useState(null);
  const compatible = (m) =>
    !hasAssignment ||
    availableMillings.some((available) => available.millingId === m.millingId);
  const pattern = search
    .trim()
    .split("*")
    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join(".*");
  const filtered = millings
    .filter((milling) => colorMaterial(milling) === selectedMaterial)
    .sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        numeric: true,
        sensitivity: "base"
      })
    )
    .flatMap((milling) => [
      {
        milling,
        variantId: "",
        name: milling.name,
        imageKey: milling.imageKey
      },
      ...(milling.variants || [])
        .filter(
          (variant) => variantMode(variant) === "manual" && variant.variantId
        )
        .map((variant) => ({
          milling,
          variantId: variant.variantId,
          name: `${milling.name} / ${variant.name}`,
          imageKey: variant.imageKey || milling.imageKey
        }))
    ])
    .filter((entry) => new RegExp(pattern, "i").test(entry.name || ""));
  const initialMillingIndex = currentMilling
    ? filtered.findIndex(
        (entry) =>
          String(entry.milling.millingId) === String(millingId) &&
          entry.variantId === (millingVariantId || "")
      )
    : -1;
  const initialPage =
    initialMillingIndex < 0
      ? 1
      : Math.floor(initialMillingIndex / (PICKER_PAGE_SIZE - 1)) + 1;
  const pagination = usePickerPage(
    filtered,
    JSON.stringify([search, selectedMaterial]),
    PICKER_PAGE_SIZE - 1,
    initialPage
  );
  const handleTypeahead = (character) => {
    searchInputRef.current?.focus();
    setSearch((currentSearch) => currentSearch + character);
  };
  usePickerKeyboardNavigation(
    pagination,
    !preview && !pendingMaterial,
    millingId === null
      ? "no-milling"
      : currentMilling
        ? `${currentMilling.millingId}:${millingVariantId || ""}`
        : null,
    handleTypeahead
  );
  const select = (milling, variantId = "") =>
    setSelection({ milling, variantId });
  const confirm = () => {
    if (selection) onSelect(selection.milling, selection.variantId);
    else if (canConfirm) onSelect(undefined, "");
    onClose();
  };

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Modal
        show
        onHide={onClose}
        keyboard={!pendingMaterial && !preview}
        size="xl"
        centered
        style={{ zIndex: 2000 }}
        enforceFocus={!preview && !pendingMaterial}
      >
        <Modal.Header closeButton className="d-flex flex-wrap gap-3">
          <Modal.Title>{t("selectMilling")}</Modal.Title>
          {onBack && (
            <Button variant="outline-primary" onClick={() => onBack(selection)}>
              {t("chooseColor")}
            </Button>
          )}
          <PickerMaterial
            materials={materials}
            value={selectedMaterial}
            onChange={(material) => {
              if (material !== selectedMaterial) setPendingMaterial(material);
            }}
          />
          <Form.Control
            ref={searchInputRef}
            type="search"
            data-picker-search
            autoFocus
            aria-label={t("search")}
            placeholder={t("search")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ marginLeft: "auto", width: 220, borderRadius: 999 }}
          />
        </Modal.Header>
        <Modal.Body
          style={{ height: "70vh", overflowY: "auto", background: "#fff" }}
        >
          {children}
          <Row
            xs={2}
            sm={3}
            md={4}
            lg={5}
            xl={6}
            className="g-3"
            data-picker-nav-group="item"
          >
            {[null, ...pagination.pageItems].map((entry) => {
              if (!entry)
                return (
                  <Col key="no-milling">
                    <PickerEmptyCard
                      label={t("noMilling")}
                      pickerKey="no-milling"
                      selected={selection?.milling === null}
                      onSelect={() => select(null)}
                    />
                  </Col>
                );
              const milling = entry.milling;
              const selected = selection
                ? selection.milling?.millingId === milling.millingId &&
                  selection.variantId === entry.variantId
                : millingId === milling.millingId &&
                  millingVariantId === entry.variantId;
              return (
                <Col key={`${milling.millingId}:${entry.variantId}`}>
                  <Card
                    className={`h-100 shadow-sm ${selected ? "border-primary" : ""}`}
                    style={{ maxWidth: 220, margin: "0 auto" }}
                  >
                    <button
                      type="button"
                      className="border-0 bg-transparent p-2 w-100"
                      data-picker-nav="item"
                      data-picker-key={`${milling.millingId}:${entry.variantId}`}
                      aria-pressed={selected}
                      onClick={() => select(milling, entry.variantId)}
                    >
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: 100 }}
                      >
                        <MillingImage
                          imageKey={entry.imageKey}
                          name={entry.name}
                        />
                      </div>
                      <div className="mt-2 text-break">{entry.name}</div>
                      {hasAssignment && (
                        <span
                          className={
                            compatible(milling) ? "text-success" : "text-danger"
                          }
                        >
                          {compatible(milling) ? "✓" : "✗"}
                        </span>
                      )}
                    </button>
                    {entry.imageKey && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setPreview(entry)}
                      >
                        {t("preview")}
                      </Button>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>
          {!filtered.length && (
            <p className="text-muted" role="status">
              {t("noMillingAvailable")}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {pagination.pageCount > 1 && (
            <div className="me-auto">
              <PickerPagination {...pagination} />
            </div>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            disabled={!selection && !canConfirm}
            data-picker-confirm
            onClick={confirm}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={pendingMaterial !== null}
        onHide={() => setPendingMaterial(null)}
        centered
        style={{ zIndex: 2200 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("millingMaterialChangeTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("millingMaterialChangeConfirm")}{" "}
          <strong>
            {selectedMaterial} ? {pendingMaterial}
          </strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPendingMaterial(null)}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setSelection(null);
              setSelectedMaterial(pendingMaterial);
              onMaterialChange?.(pendingMaterial);
              setSearch("");
              setPendingMaterial(null);
            }}
          >
            {t("millingMaterialChangeAccept")}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={!!preview}
        onHide={() => setPreview(null)}
        size="xl"
        centered
        style={{ zIndex: 2100 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{preview?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {preview && (
            <MillingImage
              imageKey={preview.imageKey}
              name={preview.name}
              enlarged
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
